/**
 * Sprint 14.1b — Close Evaluation confirmation dialog.
 *
 * The user can optionally type a free-text reason note (closure-reason
 * picker is deferred to a future sprint per Sprint 14 charter). On confirm
 * the dialog calls POST /api/v1/evaluations/{id}/close.
 */
import React from 'react';
import { v1Post } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

export function CloseEvaluationDialog({
  open,
  onOpenChange,
  evaluation,
  onClosed,
}) {
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const confirm = async () => {
    if (!evaluation?.id) return;
    setSubmitting(true);
    try {
      const closed = await v1Post(`/evaluations/${evaluation.id}/close`, {
        reason: reason.trim() || null,
      });
      toast.success(`Evaluation “${closed.name}” closed`);
      onOpenChange(false);
      onClosed?.(closed);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not close evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="close-evaluation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Close evaluation?
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-foreground">{evaluation?.name}</span> will become
            read-only. Stage actions will be disabled, but the AI Report remains viewable and
            downloadable.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-medium text-muted-foreground">
            Reason / note (optional)
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Candidate withdrew — accepted a competing offer"
            rows={3}
            data-testid="close-evaluation-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={confirm}
            disabled={submitting}
            data-testid="close-evaluation-confirm-button"
          >
            {submitting ? 'Closing…' : 'Close Evaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
